/**
 * The behaviour behind `wpp::dropdown`.
 *
 * The panel is `position: fixed` and positioned from the trigger's viewport
 * rectangle, rather than absolutely inside the trigger's box. That is what lets a
 * dropdown inside a scrolling table or a narrow card escape its container instead
 * of being clipped by it — at the cost of having to be repositioned by hand
 * whenever the trigger moves.
 *
 * One consequence worth knowing: `position: fixed` is relative to the nearest
 * ancestor with a `transform`, `filter` or `will-change`, not to the viewport. If
 * a dropdown is ever placed inside a transformed element — a slide-over that
 * animates in, say — these coordinates will be wrong. Position it inside such a
 * container only if you have checked.
 *
 * Alignment is decided against the viewport rather than asked for. Whichever
 * side a panel should hang off depends on where its trigger happens to be, and
 * that is a fact about the browser at the moment it opens — not something the
 * markup can know. A screen that states it is stating a guess, and the guess is
 * wrong at some window width.
 */
let sequence = 0;

/**
 * @param {{ clipHeight?: boolean }} options
 */
export default (options = {}) => ({
    open: false,

    /**
     * Whether the panel is capped to the space available and scrolls past it.
     *
     * Off for a panel whose content is itself absolutely positioned — a
     * multiselect's option list, say. That content never grows the panel's box,
     * so capping the box's height does not make room for it; it only scrolls
     * away what is already showing outside it.
     */
    clipHeight: options.clipHeight ?? true,

    /** Which edge of the panel lines up with the trigger, decided when it opens. */
    align: 'left',

    /**
     * The id of the control that opens the panel, so the panel can be named by
     * it. This used to read `$refs.trigger.id` — but the trigger ref is the
     * wrapper `div`, which never has an id, so the panel was labelled by the
     * empty string.
     */
    triggerId: null,

    panelStyle: 'visibility: hidden;',
    cssMaxHeight: null,
    listeners: [],

    init() {
        this.$nextTick(() => this.describeTrigger());

        const reposition = () => {
            if (! this.open) {
                return;
            }

            this.checkAlignment();
            this.positionPanel();
        };

        /*
         * The panel is fixed, so anything that moves the trigger moves it out of
         * alignment. Scroll is captured because the trigger may sit inside a
         * scrolling container rather than the document.
         */
        this.listen(window, 'resize', reposition);
        this.listen(window, 'scroll', reposition, true);

        /*
         * Close when leaving the page.
         *
         * `wire:navigate` swaps the document without a reload, and a dropdown
         * whose item was just clicked would otherwise still be open — sitting over
         * a page the user did not open it on, with no obvious way of having got
         * there. This is the "it stays open by itself" case.
         */
        this.listen(document, 'livewire:navigating', () => {
            this.open = false;
        });

        this.$nextTick(() => {
            this.$watch('open', (value) => {
                if (! value) {
                    return;
                }

                this.$nextTick(() => {
                    this.checkAlignment();
                    this.positionPanel();
                });
            });
        });
    },

    /**
     * Alpine calls this when the component is torn down. Without it, every
     * `wire:navigate` would leave another pair of window listeners behind, each
     * holding a reference to a component that no longer exists.
     */
    destroy() {
        this.listeners.forEach(({ target, event, handler, capture }) => {
            target.removeEventListener(event, handler, capture);
        });

        this.listeners = [];
    },

    listen(target, event, handler, capture = false) {
        target.addEventListener(event, handler, capture);
        this.listeners.push({ target, event, handler, capture });
    },

    /**
     * Hang the panel off whichever edge leaves it on screen.
     *
     * Left by default, because a menu reading left-to-right from its trigger is
     * what most people expect; right when a left-aligned panel would run past
     * the window, and back to left if right would run past the other side —
     * which happens when the panel is wider than the space either way, and where
     * the clamp in `positionPanel` then takes over.
     */
    checkAlignment() {
        if (! this.$refs.trigger || ! this.$refs.panel) {
            return;
        }

        const margin = 8;
        const triggerRect = this.$refs.trigger.getBoundingClientRect();
        const panelWidth = this.$refs.panel.offsetWidth;

        const overflowsRight = triggerRect.left + panelWidth > window.innerWidth - margin;
        const rightAlignedOverflowsLeft = triggerRect.right - panelWidth < margin;

        this.align = overflowsRight && ! rightAlignedOverflowsLeft ? 'right' : 'left';
    },

    positionPanel() {
        if (! this.$refs.trigger || ! this.$refs.panel) {
            return;
        }

        const margin = 8; // The gap between trigger and panel.
        const triggerRect = this.$refs.trigger.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        /*
         * Cache any CSS-defined max-height cap (`max-h-96` from `fixed-height`)
         * before overriding max-height inline, so we never grow taller than the
         * markup intended.
         */
        if (this.cssMaxHeight === null) {
            const parsed = parseInt(window.getComputedStyle(this.$refs.panel).maxHeight, 10);
            this.cssMaxHeight = Number.isNaN(parsed) ? Infinity : parsed;
        }

        const panelWidth = this.$refs.panel.offsetWidth;
        const naturalHeight = this.$refs.panel.scrollHeight;

        let left = this.align === 'right'
            ? triggerRect.right - panelWidth
            : triggerRect.left;

        // Keep it on screen horizontally whichever side it ended up on.
        if (left < margin) {
            left = margin;
        } else if (left + panelWidth > windowWidth - margin) {
            left = Math.max(margin, windowWidth - panelWidth - margin);
        }

        // Prefer below the trigger, but flip above when there is more room there.
        const spaceBelow = windowHeight - triggerRect.bottom - margin;
        const spaceAbove = triggerRect.top - margin;
        const placeBelow = naturalHeight <= spaceBelow || spaceBelow >= spaceAbove;

        const available = placeBelow ? spaceBelow : spaceAbove;
        const maxHeight = Math.max(0, Math.min(this.cssMaxHeight, available));

        let top;

        if (placeBelow) {
            top = triggerRect.bottom + margin;
        } else {
            top = triggerRect.top - Math.min(naturalHeight, maxHeight) - margin;

            if (top < margin) {
                top = margin;
            }
        }

        const heightStyle = this.clipHeight
            ? `max-height: ${maxHeight}px; overflow-y: auto; `
            : '';

        this.panelStyle = `top: ${top}px; left: ${left}px; ${heightStyle}visibility: visible;`;
    },

    toggle() {
        if (this.open) {
            return this.close();
        }

        this.open = true;
    },

    close(focusTrigger = false) {
        if (! this.open) {
            return;
        }

        this.open = false;

        if (focusTrigger) {
            this.focusTrigger();
        }
    },

    /**
     * The control inside the trigger wrapper — the thing that takes focus.
     *
     * The wrapper is a `div` with a click handler on it. `aria-expanded`,
     * `aria-controls` and `aria-haspopup` were on that wrapper, which no screen
     * reader user ever lands on, so the button they *do* land on announced
     * itself as an ordinary button that does nothing in particular.
     */
    get triggerControl() {
        return this.$refs.trigger?.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) ?? this.$refs.trigger ?? null;
    },

    /**
     * Describe the trigger once, then keep `aria-expanded` in step with it.
     */
    describeTrigger() {
        const control = this.triggerControl;

        if (! control) {
            return;
        }

        if (! control.id) {
            sequence += 1;
            control.id = `wpp-dropdown-trigger-${sequence}`;
        }

        this.triggerId = control.id;

        control.setAttribute('aria-haspopup', 'menu');
        control.setAttribute('aria-expanded', this.open ? 'true' : 'false');

        if (this.$refs.panel?.id) {
            control.setAttribute('aria-controls', this.$refs.panel.id);
        }

        this.$watch('open', (value) => {
            control.setAttribute('aria-expanded', value ? 'true' : 'false');
        });
    },

    /**
     * Return focus to the control that opened the panel.
     *
     * The trigger ref is a wrapper `div`, which is not focusable, so focusing it
     * directly silently does nothing and leaves focus on the document body — the
     * classic "pressed Escape and lost my place" bug. Focus what is actually
     * inside it.
     */
    focusTrigger() {
        this.triggerControl?.focus?.();
    },
});
